import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { isAssistantLoggedIn } from '@/lib/permissions';
import type { ReceiptEditHistory } from '@/types/entities';

/**
 * Hook to fetch edit history for a specific receipt
 * Supports both admin users and assistants with stock view permission
 */
export function useReceiptEditHistory(receiptId: string | null, restaurantId: string | null) {
  return useQuery({
    queryKey: ['receipt-edit-history', receiptId],
    queryFn: async (): Promise<ReceiptEditHistory[]> => {
      if (!receiptId || !restaurantId) return [];

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // Use assistant RPC function (cast to any since types may not be generated yet)
        const { data, error } = await (supabase.rpc as any)('assistant_get_receipt_edit_history', {
          p_restaurant_id: restaurantId,
          p_receipt_id: receiptId,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view edit history');
            return [];
          }
          console.error('Error fetching receipt edit history:', error);
          throw error;
        }

        return ((data as any[]) || []).map((h: any): ReceiptEditHistory => ({
          id: h.id,
          receiptId: h.receipt_id,
          restaurantId: h.restaurant_id,
          fieldChanged: h.field_changed,
          oldValue: h.old_value,
          newValue: h.new_value,
          editedAt: h.edited_at,
          editedBy: h.edited_by,
          editedByAssistantId: h.edited_by_assistant_id,
          modifiedBy: h.modified_by,
          createdAt: h.created_at,
        }));
      }

      // Admin user - use direct query with assistant info
      const { data, error } = await supabase
        .from('receipt_edit_history')
        .select(`
          *,
          assistants:edited_by_assistant_id (
            username
          )
        `)
        .eq('receipt_id', receiptId)
        .order('edited_at', { ascending: false });

      if (error) {
        console.error('Error fetching receipt edit history:', error);
        throw error;
      }

      return (data || []).map((h: any): ReceiptEditHistory => ({
        id: h.id,
        receiptId: h.receipt_id,
        restaurantId: h.restaurant_id,
        fieldChanged: h.field_changed,
        oldValue: h.old_value,
        newValue: h.new_value,
        editedAt: h.edited_at,
        editedBy: h.edited_by,
        editedByAssistantId: h.edited_by_assistant_id,
        modifiedBy: h.edited_by_assistant_id
          ? (h.assistants?.username || 'Unknown assistant')
          : (h.edited_by ? 'Admin' : 'Unknown'),
        createdAt: h.created_at,
      }));
    },
    enabled: !!receiptId && !!restaurantId,
  });
}

